/**
 * Parse a UTC date string into a local Date object
 * @param {string} dateString - UTC date string from backend (format: "YYYY-MM-DD HH:mm:ss")
 * @returns {Date} Local date object
 */
function parseUTCDate(dateString) {
    if (!dateString) return null
    try {
        // Ensure the date string is treated as UTC
        // Add 'Z' to indicate UTC if the string doesn't have a timezone
        const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`
        return new Date(utcString)
    } catch {
        console.warn('Invalid date format:', dateString)
        return null
    }
}

/**
 * Format a date string to a relative time (e.g., "2h ago", "3d ago")
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted relative time
 */
export function formatRelativeTime(dateString) {
    try {
        const date = parseUTCDate(dateString)
        if (!date) return 'Unknown'

        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMins < 1) return 'now'
        if (diffMins < 60) return `${diffMins}m`
        if (diffHours < 24) return `${diffHours}h`
        if (diffDays < 7) return `${diffDays}d`

        return date.toLocaleDateString()
    } catch {
        return 'Unknown'
    }
}

/**
 * Check if a date is today in local time
 * @param {string} dateString - ISO date string
 * @returns {boolean} True if the date is today
 */
export function isToday(dateString) {
    try {
        const date = parseUTCDate(dateString)
        if (!date) return false

        const today = new Date()
        return date.toDateString() === today.toDateString()
    } catch {
        return false
    }
}

/**
 * Format a date to a readable format in local time
 * @param {string} dateString - ISO date string
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export function formatDate(dateString, options = {}) {
    try {
        const date = parseUTCDate(dateString)
        if (!date) return 'Unknown'

        const defaultOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            ...options,
        }
        return new Intl.DateTimeFormat(undefined, defaultOptions).format(date)
    } catch {
        return 'Unknown'
    }
}

/**
 * Format a date string to dd/MM format in local time
 * @param {string} dateString - UTC date string
 * @returns {string} Formatted date (dd/MM)
 */
export function formatMatchDate(dateString) {
    try {
        const date = parseUTCDate(dateString)
        if (!date) return ''

        const formatter = new Intl.DateTimeFormat(undefined, {
            day: '2-digit',
            month: '2-digit',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
        return formatter.format(date)
    } catch {
        console.warn('Invalid date format:', dateString)
        return ''
    }
}

/**
 * Format a date string to HH:mm format in local time
 * @param {string} dateString - UTC date string
 * @returns {string} Formatted time (HH:mm)
 */
export function formatMatchTime(dateString) {
    try {
        const date = parseUTCDate(dateString)
        if (!date) return ''

        const formatter = new Intl.DateTimeFormat(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
        return formatter.format(date)
    } catch {
        console.warn('Invalid date format:', dateString)
        return ''
    }
}
